import { CalendarDays, ClipboardPlus, Search, CircleX, Plus } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import DataTablePagination from '../DataTablePagination';
import Filter from '../Filter';
import Spacing from '../Spacing';
import AIChartingDataTable from '../DataTable/AIChartingDataTable';
import TabMenu from '../TabMenu';
import DatePicker from 'react-datepicker';
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { can_visit, debounce, getPractice, notify, numberWithCommas } from "../../helpers";
import { convertDateWithSlash, formatDate, exchangeDate } from "../../helpers";
import { fetchAIChartings, loadVisitType } from "../../services/aiCharting.js";
import SaveFilter from '../Filter/SaveFilter.jsx';
import FilterSummary from '../UI/FilterSummary.jsx';
import { fetchFilterData } from '../../services/saveFilter.js';
import FilterSaveConfirmationModal from '../Modal/FilterSaveConfirmationModal.jsx';
import { Link } from 'react-router-dom';


export default function AICharting({selectedPracticeChange, fetchChangePracticeData, setSmsLimit}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [aiChartings, setAIChartings] = useState([]);

  const defaultFilter = {
    page: 1,
    per_page: 10,
    status: "",
    chart_type: "",
    search_term: "",
    dos: "",
    first_name: "",
    last_name: "",
    dob: "",
    sex: "",
    mobile_phone: "",
    start_age: "",
    end_age: "",
    insurance_ids: [],
    city: "",
    state: "",
    zip: "",
    account_id: account_id,
    location_id: location_id,
    order_by: [],
    vt_name: ""
  };
  const [filter, setFilter] = useState(defaultFilter);
  const [selectedInsuranceOptions, setSelectedInsuranceOptions] = useState([]);
  const [patientFormData, setPatientFormData] = useState(filter);
  const [isFilterCleared, setIsFilterCleared] = useState(false);
  const [showSave, setShowSave] = useState(true);
  const [saveConfirmModal, setSaveConfirmModal] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [vtOptions, setVtOptions] = useState([]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const handleClearFilters = () => {
    setIsFilterCleared(true);
  };

  const handleResetFilterStatus = () => {
    setIsFilterCleared(false);
  };

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
    doneCount: 0,
    pendingCount: 0,
    account_id: account_id,
    location_id: location_id,
  });

  const handleDate = (date) => {
    let dos = formatDate(date);
    setFilter({
      ...filter,
      dos: exchangeDate(dos),
    });
    setDataLoading(true);
  };

  const getAIChartingByStatus = (selectedTabValue) => {
    const tab = selectedTabValue.split(" ")[0];
    const tabValue = tab === "All" ? "" : tab;
    setFilter({ ...filter, status: tabValue, page: 1 });
  };

  const loadAICharting = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const chartingResponse = await fetchAIChartings(filter);
      setAIChartings(chartingResponse.data.results);

      setPaginationCount({
        totalCount: chartingResponse.data.count,
        totalPages: chartingResponse.data.total_pages,
        doneCount: chartingResponse.data.done,
        pendingCount: chartingResponse.data.pending,
      }); // [setPaginationCount] updated state with charting count
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadAICharting ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const tabMenuData = [
    `All (${paginationCount.totalCount})`,
    `Done (${paginationCount.doneCount})`,
    `Pending (${paginationCount.pendingCount})`,
  ];

  useEffect(() => {
    loadAICharting(filter);
  }, [filter]);
  
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  // Load All Visit Type
  const loadAllVisitType = async () => {
    try {
      const response = await loadVisitType();
      if (response?.data) {
        const vtData = response?.data?.map((items) => ({
          value: items.vt_name,
          label: items.vt_name
        }));
        setVtOptions(vtData);
      }
    } catch (error) {
      // console.log("🚀 ~ loadAllVisitType ~ error:", error);
    }
  };

  const clearFilterField = (field) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: "",  // Clear the specified field in filter state
    }));
    setPatientFormData((prevData) => ({
      ...prevData,
      [field]: "",  // Clear the specified field in patientFormData
    }));
  };
  
  const loadFilters = async () => {
    const filters = {
      account_id: account_id,
      location_id: location_id,
      user_id: user_id,
      filter_type: "ai-charting",
    };

    try {
      const response = await fetchFilterData(filters);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          setFilterData(response?.data);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ loadFilters ~ error:", error);
    }
  };

  useEffect(() => {
    loadFilters();
    loadAllVisitType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, location_id]);

  useDocumentTitle("AI Charting");
  
  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_iris_bg docpro_white_color">
            <ClipboardPlus width={20} height={20} />
          </i>
          AI Charting
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
        <div className="docpro_page_heading_btns">
          {can_visit('ai_charting_docflow') && (
            <Link to="/docflow">
              <button className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn">
                <Plus width={20} height={20} />
                <span>DocFlow</span>
              </button>
            </Link>
          )}
        </div>
      </div>

      <Spacing lg="4" sm="4" />
      <TabMenu tabMenuData={tabMenuData} filter={false} handleSelectTab={getAIChartingByStatus}setDataLoading={setDataLoading}/>
      <Spacing lg="12" sm="12" />

      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings small_heading">
          <div className="searching_div searching_div2 docpro_grid_gap_10">
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>

            <div className="filter_date2">
              <div
                className={`docpro_toggle_calendar ${
                  filter.dos ? "active" : ""
                }`}
              >
                <CalendarDays />
                <DatePicker
                  selected={
                    filter.dos ? new Date(filter.dos) : new Date()
                  }
                  onChange={(date) => handleDate(date)}
                />
                {filter.dos && (
                  <span>{convertDateWithSlash(filter.dos)}</span>
                )}
              </div>
              {filter.dos && (
                <span className='pointer' onClick={() => handleDate("")}>
                  <CircleX size={22} color="var(--accent)" />
                </span>
              )}
            </div>
          </div>

          <div className="docpro_data_table_btns small_filter_btns2 p-0">
            {filterData?.length > 0 && (
              <SaveFilter
                account_id={account_id}
                location_id={location_id}
                user_id={user_id}
                filterData={filterData}
                setDataLoading={setDataLoading}
                setPatientFormData={setPatientFormData}
                setSelectedInsuranceOptions={setSelectedInsuranceOptions}
                setFilter={setFilter}
                setShowSave={setShowSave}
                loadFilters={loadFilters}
                reloadData={loadAICharting}
                reloadDataFilter={defaultFilter}
                previousScrollPosition={previousScrollPosition}
                setPreviousScrollPosition={setPreviousScrollPosition}
              />
            )}
            <Filter
              filterType="ai_patient"
              reloadData={loadAICharting}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              isFilterCleared={isFilterCleared}
              onResetFilterStatus={handleResetFilterStatus}
              selectedInsuranceOptions={selectedInsuranceOptions}
              setSelectedInsuranceOptions={setSelectedInsuranceOptions}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              setShowSave={setShowSave}
              vtOptions={vtOptions}
            />
          </div>
        </div>

        <div className="docpro_filter_summary">
          <FilterSummary 
            filter={filter}
            setFilter={setFilter}
            loadData={loadAICharting}
            onClearFilters={handleClearFilters}
            setSelectedInsuranceOptions={setSelectedInsuranceOptions}
            clearFilterField={clearFilterField}
            patientFormData={patientFormData}
            setPatientFormData={setPatientFormData}
            setSaveConfirmModal={setSaveConfirmModal}
            showSave={showSave}
            setShowSave={setShowSave}
            setPreviousScrollPosition={setPreviousScrollPosition}
            setDataLoading={setDataLoading}
          /> 
        </div>
        <AIChartingDataTable
          tableData={aiChartings}
          reloadData={loadAICharting}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
        />
        {(aiChartings.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {saveConfirmModal && (
        <FilterSaveConfirmationModal
          account_id={account_id}
          location_id={location_id}
          user_id={user_id}
          filter_type="ai-charting"
          filter={filter}
          openModal={saveConfirmModal}
          setOpenModal={setSaveConfirmModal}
          setShowSave={setShowSave}
          loadFilters={loadFilters}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
